import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, CreditCard, ShoppingCart, RotateCcw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '@/lib/api';

export default function Analytics() {
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/analytics/dashboard?period=${period}`);
        setData(res.data);
      } catch (err) {
        console.error('Analytics error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [period]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatChartDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white px-3 py-2 text-sm">
          <p className="font-medium">{label}</p>
          <p className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {formatCurrency(payload[0].value)}
          </p>
          <p className="text-slate-400 text-xs">{payload[0].payload.count} transactions</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="h-32 bg-slate-100 animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data?.role_filtered
              ? 'Showing your transactions only'
              : 'Payment performance overview'}
          </p>
        </div>

        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList data-testid="period-selector">
            <TabsTrigger value="1d" data-testid="period-1d">Today</TabsTrigger>
            <TabsTrigger value="7d" data-testid="period-7d">7 Days</TabsTrigger>
            <TabsTrigger value="30d" data-testid="period-30d">30 Days</TabsTrigger>
            <TabsTrigger value="90d" data-testid="period-90d">90 Days</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border" data-testid="analytics-stats">
        <Card className="border-0 shadow-none rounded-none">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-wider text-slate-400 font-bold">Revenue</span>
            </div>
            <p className="text-xl md:text-2xl font-extrabold" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="total-revenue">
              {formatCurrency(data?.total_revenue || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-none rounded-none">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-blue-500" />
              <span className="text-xs uppercase tracking-wider text-slate-400 font-bold">Paid</span>
            </div>
            <p className="text-xl md:text-2xl font-extrabold" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="paid-count">
              {data?.paid_transactions || 0}
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-none rounded-none">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingCart className="w-4 h-4 text-orange-500" />
              <span className="text-xs uppercase tracking-wider text-slate-400 font-bold">Avg Basket</span>
            </div>
            <p className="text-xl md:text-2xl font-extrabold" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="analytics-avg-basket">
              {formatCurrency(data?.avg_basket || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-none rounded-none">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-2 mb-2">
              <RotateCcw className="w-4 h-4 text-slate-500" />
              <span className="text-xs uppercase tracking-wider text-slate-400 font-bold">Refunds</span>
            </div>
            <p className="text-xl md:text-2xl font-extrabold" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="refund-count">
              {data?.refunded_count || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card className="border-slate-200 p-4 md:p-6" data-testid="revenue-chart-card">
        <h3 className="text-base font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Daily Revenue
        </h3>
        {data?.daily_breakdown?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.daily_breakdown.map(d => ({ ...d, label: formatChartDate(d.date) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }} />
              <YAxis tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" fill="hsl(25, 95%, 53%)" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
            No data for this period
          </div>
        )}
      </Card>

      {/* Status Breakdown */}
      {data?.status_breakdown && Object.keys(data.status_breakdown).length > 0 && (
        <Card className="border-slate-200 p-4 md:p-6" data-testid="status-breakdown">
          <h3 className="text-base font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Status Breakdown
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(data.status_breakdown).map(([status, count]) => (
              <div key={status} className="bg-slate-50 p-3 border border-slate-100">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">{status}</div>
                <div className="text-lg font-extrabold mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{count}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
